import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import Navbar from '../components/Navbar';
import SettingsModal from '../components/SettingsModal';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Plus, 
  ArrowLeft,
  Package,
  Clock,
  CheckCircle2,
  XCircle,
  DollarSign,
  Loader2,
  MapPin,
  Phone,
  Wifi,
  WifiOff
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Orders = () => {
  const { outletId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const { isConnected, subscribeToOrders } = useWebSocket();
  
  const [outlet, setOutlet] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeader();
      const [outletRes, ordersRes] = await Promise.all([
        axios.get(`${API}/outlets/${outletId}`, { headers }),
        axios.get(`${API}/orders/outlet/${outletId}`, { headers })
      ]);
      
      setOutlet(outletRes.data);
      setOrders(ordersRes.data);
    } catch (e) {
      console.error('Failed to fetch orders:', e);
      toast.error('Failed to load orders');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [outletId, getAuthHeader, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Subscribe to real-time order updates
  useEffect(() => {
    if (!subscribeToOrders) return;
    
    const unsubscribe = subscribeToOrders(outletId, (data) => {
      if (data.action === 'created') {
        // Add new order to the list
        setOrders(prev => [data.order, ...prev]);
        toast.success('New order received!', { duration: 3000 });
      } else if (data.action === 'updated') {
        // Update existing order
        setOrders(prev => prev.map(o => 
          o.id === data.order_id ? data.order : o
        ));
        toast.info('Order updated', { duration: 2000 });
      } else if (data.action === 'deleted') {
        // Remove deleted order
        setOrders(prev => prev.filter(o => o.id !== data.order_id));
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [outletId, subscribeToOrders]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new':
        return <Badge className="status-new">New</Badge>;
      case 'delivered_paid':
        return <Badge className="status-delivered-paid">Delivered & Paid</Badge>;
      case 'delivered_no_payment':
        return <Badge className="status-delivered-no-payment">Delivered (No Payment)</Badge>;
      case 'rejected':
        return <Badge className="status-rejected">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new':
        return <Clock className="h-5 w-5 text-blue-500" />;
      case 'delivered_paid':
        return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
      case 'delivered_no_payment':
        return <DollarSign className="h-5 w-5 text-amber-500" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Package className="h-5 w-5" />;
    }
  };

  // Separate orders by status
  // Active: new orders + delivered but NOT paid (needs verification)
  const activeOrders = orders.filter(o => {
    if (o.status === 'delivered_paid' || o.status === 'rejected') return false;
    return true;
  });
  // History: completed (delivered_paid) + rejected
  const completedOrders = orders.filter(o => ['delivered_paid', 'rejected'].includes(o.status));

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="orders-page">
      <Navbar onOpenSettings={() => setSettingsOpen(true)} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">{outlet?.name}</h1>
                {/* Real-time connection indicator */}
                {isConnected ? (
                  <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-500/10 px-2 py-1 rounded-full">
                    <Wifi className="h-3 w-3" />
                    Live
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                    <WifiOff className="h-3 w-3" />
                    Offline
                  </span>
                )}
              </div>
              {outlet?.company_reg_no && (
                <p className="text-sm font-mono text-muted-foreground">#{outlet.company_reg_no}</p>
              )}
            </div>
          </div>
          <Button onClick={() => navigate(`/orders/${outletId}/new`)} data-testid="new-order-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Order
          </Button>
        </div>

        {/* Orders Tabs */}
        <Tabs defaultValue="active" className="space-y-4">
          <TabsList>
            <TabsTrigger value="active" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Active ({activeOrders.length})
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              History ({completedOrders.length})
            </TabsTrigger>
          </TabsList>

          {/* Active Orders */}
          <TabsContent value="active">
            {activeOrders.length === 0 ? (
              <Card className="p-12 text-center">
                <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Active Orders</h3>
                <p className="text-muted-foreground mb-4">Create a new order to get started.</p>
                <Button onClick={() => navigate(`/orders/${outletId}/new`)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Order
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {activeOrders.map((order) => (
                  <Card 
                    key={order.id} 
                    className="card-hover cursor-pointer"
                    onClick={() => navigate(`/orders/${outletId}/${order.id}`)}
                    data-testid={`order-${order.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          {getStatusIcon(order.status)}
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-sm font-semibold">
                                #{order.id.slice(0, 8).toUpperCase()}
                              </span>
                              {getStatusBadge(order.status)}
                              {order.payment_method ? (
                                order.payment_status === 'paid' ? (
                                  <Badge variant="outline" className="text-xs text-emerald-500 border-emerald-500/30 capitalize">
                                    <DollarSign className="h-3 w-3 mr-1" />
                                    {order.payment_method} - Paid
                                  </Badge>
                                ) : order.payment_status === 'collect' ? (
                                  <span className="text-xs text-red-500 font-bold">Cash - Collect</span>
                                ) : order.payment_status === 'upon_delivery' ? (
                                  <span className="text-xs text-red-500 font-bold">Transfer - Upon Delivery</span>
                                ) : (
                                  <Badge variant="outline" className="text-xs capitalize">
                                    <DollarSign className="h-3 w-3 mr-1" />
                                    {order.payment_method}
                                  </Badge>
                                )
                              ) : (
                                <Badge variant="outline" className="text-xs text-amber-500 border-amber-500/30">
                                  Unpaid
                                </Badge>
                              )}
                            </div>
                            {order.item_details && (
                              <p className="text-sm font-medium">
                                {order.item_details}
                                {order.quantity > 1 && <span className="text-muted-foreground ml-1">x{order.quantity}</span>}
                              </p>
                            )}
                            {!order.item_details && order.quantity > 1 && (
                              <p className="text-sm text-muted-foreground">Qty: {order.quantity}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {order.location}
                              </span>
                              <a
                                href={`tel:${order.contact_no}`}
                                onClick={(e) => e.stopPropagation()}
                                onTouchEnd={(e) => e.stopPropagation()}
                                className="flex items-center gap-1 text-primary hover:underline active:underline"
                                data-testid={`call-${order.id}`}
                              >
                                <Phone className="h-3 w-3" />
                                {order.contact_no}
                              </a>
                            </div>
                            {order.delivery_status && (
                              <p className={`text-sm mt-2 ${order.delivery_status === 'delivered' && order.payment_status !== 'paid' ? 'text-red-500 font-bold' : 'text-primary'}`}>
                                {order.delivery_status === 'delivered' 
                                  ? (order.payment_status !== 'paid' ? 'Delivered - Verify Payment' : 'Delivered')
                                  : order.delivery_status === 'not_delivered' 
                                    ? `Not Delivered${order.delivery_reason ? ` - ${order.delivery_reason}` : ''}`
                                    : order.delivery_status}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-sm text-muted-foreground">
                          <p>{new Date(order.created_at).toLocaleDateString()}</p>
                          <p>{new Date(order.created_at).toLocaleTimeString()}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* History */}
          <TabsContent value="history">
            {completedOrders.length === 0 ? (
              <Card className="p-12 text-center">
                <CheckCircle2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Order History</h3>
                <p className="text-muted-foreground">Completed and rejected orders will appear here.</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {completedOrders.map((order) => (
                  <Card 
                    key={order.id} 
                    className="card-hover cursor-pointer opacity-80"
                    onClick={() => navigate(`/orders/${outletId}/${order.id}`)}
                    data-testid={`order-${order.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          {getStatusIcon(order.status)}
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-sm font-semibold">
                                #{order.id.slice(0, 8).toUpperCase()}
                              </span>
                              {getStatusBadge(order.status)}
                              {order.payment_method && (
                                order.payment_status === 'paid' ? (
                                  <Badge variant="outline" className="text-xs text-emerald-500 border-emerald-500/30 capitalize">
                                    <DollarSign className="h-3 w-3 mr-1" />
                                    {order.payment_method} - Paid
                                  </Badge>
                                ) : order.payment_status === 'collect' ? (
                                  <span className="text-xs text-red-500 font-bold">Cash - Collect</span>
                                ) : order.payment_status === 'upon_delivery' ? (
                                  <span className="text-xs text-red-500 font-bold">Transfer - Upon Delivery</span>
                                ) : (
                                  <Badge variant="outline" className="text-xs capitalize">
                                    <DollarSign className="h-3 w-3 mr-1" />
                                    {order.payment_method}
                                  </Badge>
                                )
                              )}
                            </div>
                            {order.item_details && (
                              <p className="text-sm font-medium">
                                {order.item_details}
                                {order.quantity > 1 && <span className="text-muted-foreground ml-1">x{order.quantity}</span>}
                              </p>
                            )}
                            {!order.item_details && order.quantity > 1 && (
                              <p className="text-sm text-muted-foreground">Qty: {order.quantity}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {order.location}
                              </span>
                              <a
                                href={`tel:${order.contact_no}`}
                                onClick={(e) => e.stopPropagation()}
                                onTouchEnd={(e) => e.stopPropagation()}
                                className="flex items-center gap-1 text-primary hover:underline active:underline"
                              >
                                <Phone className="h-3 w-3" />
                                {order.contact_no}
                              </a>
                            </div>
                          </div>
                        </div>
                        <div className="text-right text-sm text-muted-foreground">
                          <p>{new Date(order.created_at).toLocaleDateString()}</p>
                          <p>{new Date(order.created_at).toLocaleTimeString()}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
};

export default Orders;
